/* eslint-disable max-lines */
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import FormFieldsContext from '@react-form-fields/material-ui/components/Context';
import FieldDate from '@react-form-fields/material-ui/components/Date';
import FormValidation from '@react-form-fields/material-ui/components/FormValidation';
import FieldText from '@react-form-fields/material-ui/components/Text';
import ConfigBuilder from '@react-form-fields/material-ui/config/builder';
import lang from '@react-form-fields/material-ui/lang/pt-br';
import { theme } from 'assets/theme';
import Axios from 'axios';
import { FormComponent, IStateForm } from 'components/Abstract/Form';
import ErrorMessage from 'components/Shared/ErrorMessage';
import FileReader from 'components/Shared/FileSelector/FileReader';
import Snackbar from 'components/Shared/Snackbar';
import { WithStyles } from 'decorators/withStyles';
import { formatToBase64 } from 'formatters/formatToBase64';
import { decimal } from 'formatters/masks';
import { IVenture } from 'interfaces/venture';
import React from 'react';
import RxOp from 'rxjs-operators';
import ventureService from 'services/venture';

/* eslint-disable max-lines */

const fieldConfig = new ConfigBuilder()
  .fromLang(lang)
  .addMask('number',
    value => decimal.apply(value), value => decimal.clean(value))
  .build();

interface IState extends IStateForm<IVenture> {
 loading: boolean;
 error?: null;
}

interface IProps {
 opened: boolean;
 venture?: IVenture;
 onComplete: (venture: IVenture) => void;
 onCancel: () => void;
 classes?: any;
}

@WithStyles({
  img: {
    maxWidth: '100%',
    margin: 'auto',
    display: 'block',
    padding: 5,
    boxShadow: theme.shadows['5'],
  },
  content: {
    width: 1700,
    height: 700,
    maxWidth: '100%',
  },
  dialogActions: {
    padding: theme.spacing(2),
  },
})

export default class VentureFormDialog extends FormComponent<IProps, IState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      ...this.state,
      loading: true,
    };
  }

  get isEdit(): boolean {
    return !!this.state.model.id;
  }

 handleEnter = () => {
   const venture = this.props.venture || {};
   const model: IState['model'] = Object.keys(venture).reduce(
     (parsed, key) => ({
       ...parsed,
       [key]: String(venture[key] || ''),
     }),
     {}
   );

   this.setState({ model });
   this.loadData();
 };

 handleExit = () => {
   this.resetForm();
 };

 handleCEP = async (cep: string) => {
   const { model } = this.state;
   if (cep !== model.cep) {
     this.updateModel((model, v) => (model.cep = v))(cep);
     if (cep.length === 8) {
       this.setState({ loading: true });
       const { data } = await Axios.get(`https://viacep.com.br/ws/${cep}/json/`);
       if (data.erro) {
         this.setState({ loading: false });
         return Snackbar.error('Nenhum endereço encontrado para o CEP informado');
       }
       const address = `${data.logradouro}, ${data.complemento}, ${data.bairro}`;
       const { localidade: city, uf } = data;
       this.setState({
         model: { ...model, address, city, uf, cep },
         loading: false,
       });
     }
   }
 };

 loadData = () => {
   this.setState({ loading: false, error: null });
 };

 onSubmit = (isValid: boolean) => {
   const { model } = this.state;

   if (!isValid) return;

   this.setState({ loading: true });

   ventureService
     .save(model as IVenture)
     .pipe(RxOp.logError(), RxOp.bindComponent(this))
     .subscribe(
       (venture) => {
         Snackbar.show(`${venture.name} foi salvo com sucesso!`);
         this.setState({ loading: false });

         this.props.onComplete(venture);
       },
       (err) => {
         Snackbar.error(err);
         this.setState({ loading: false });
       }
     );
 };

 cleanMaskedFields = () => {
   return [
     this.state.model.cnpj,
     this.state.model.cep,
     this.state.model.telphone,
   ].map((field) => String(field).replace(/[^\d]/g, ''));
 };

 closeFileSelector = async (file: File) => {
   const logo = await formatToBase64(file);
   this.setState((state) => ({ ...state, model: { ...state.model, logo } }));
 };

 render() {
   const { model, loading, error } = this.state;
   const { opened, classes, onCancel } = this.props;
   const [cnpj, cep, telphone] = this.cleanMaskedFields();

   return (
     <Dialog
       open={opened}
       disableBackdropClick
       disableEscapeKeyDown
       onEnter={this.handleEnter}
       onExited={this.handleExit}
     >
       {loading && <LinearProgress color="secondary" />}
       <FormFieldsContext config={fieldConfig}>
         <FormValidation onSubmit={this.onSubmit} ref={this.formValidationRef}>
           <DialogTitle>{this.isEdit ? 'Editar' : 'Novo'} Empreendimento</DialogTitle>
           <DialogContent className={classes.content}>
             {error && <ErrorMessage error={error} tryAgain={this.loadData} />}

             {!error && (
               <Grid container>
                 <Grid item xs={12}>
                   <Typography variant="subtitle1" color="primary">
          Logotipo
                   </Typography>
                   <Typography>
          * Proporção recomendada para a imagem: 760x500px
                   </Typography>
                 </Grid>
                 <Grid item xs={12}>
                   <FileReader
                     droppable
                     fileSelected={this.closeFileSelector}
                     imageURL={model.logo}
                   />
                 </Grid>
                 <Grid item xs={12}>
                   <Typography variant="subtitle1" color="primary">
          Informações
                   </Typography>
                 </Grid>

                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Nome"
                     validation="required|string|min:3|max:100"
                     disabled={loading}
                     value={model.name}
                     onChange={this.updateModel((model, v) => (model.name = v))}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Razão Social"
                     validation="string|min:3"
                     disabled={loading}
                     value={model.socialReason}
                     onChange={this.updateModel((model, v) => (model.socialReason = v))}
                   />
                 </Grid>

                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Site"
                     validation="string|min:3"
                     disabled={loading}
                     value={model.site}
                     onChange={this.updateModel((model, v) => (model.site = v))}
                   />
                 </Grid>

                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="CNPJ"
                     disabled={loading}
                     value={cnpj}
                     mask="cnpj"
                     onChange={this.updateModel((model, v) => (model.cnpj = v))}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Telefone/contato"
                     disabled={loading}
                     value={telphone}
                     mask="phone"
                     validation="string|min:8|max:15"
                     onChange={this.updateModel((model, v) => (model.telphone = v))}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldDate
                     label="Inauguração"
                     disabled={loading}
                     value={model.inauguration ? new Date(model.inauguration) : new Date()}
                     onChange={this.updateModel((model, v) => (model.inauguration = v))}
                   />
                 </Grid>
                 <Grid item xs={12}>
                   <Typography variant="subtitle1" color="primary">
          Lojas
                   </Typography>
                 </Grid>

                 <Grid item xs={12} sm={4}>
                   <FieldText
                     label="Lojas"
                     disabled={loading}
                     value={model.stores}
                     validation="string"

                     onChange={this.updateModel((model, v) => {
                       if (!v) return model.stores = '0';
                       const formatted = parseInt(v).toLocaleString('pt-BR');
                       return model.stores = formatted || '0';
                     })}
                   />
                 </Grid>
                 <Grid item xs={12} sm={4}>
                   <FieldText
                     label="Mega Lojas"
                     disabled={loading}
                     value={model.megaStores}
                     validation="string"
                     onChange={this.updateModel((model, v) => {
                       if (!v) return model.megaStores = '0';
                       const formatted = parseInt(v).toLocaleString('pt-BR');
                       return model.megaStores = formatted || '0';
                     })}
                   />
                 </Grid>
                 <Grid item xs={12} sm={4}>
                   <FieldText
                     label="Lojas Âncoras"
                     disabled={loading}
                     value={model.store}
                     validation="string"
                     onChange={this.updateModel((model, v) => {
                       if (!v) return model.store = '0';
                       const formatted = parseInt(v).toLocaleString('pt-BR');
                       return model.store = formatted || '0';
                     })}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Cinema (salas)"
                     disabled={loading}
                     value={model.movieTheaters}
                     validation="string"
                     onChange={this.updateModel((model, v) => {
                       if (!v) return model.movieTheaters = '0';
                       const formatted = parseInt(v).toLocaleString('pt-BR');
                       return model.movieTheaters = formatted || '0';
                     })}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Vagas de estacionamento"
                     disabled={loading}
                     value={model.parking}
                     validation="string"
                     onChange={this.updateModel((model, v) => {
                       if (!v) return model.parking = '0';
                       const formatted = parseInt(v).toLocaleString('pt-BR');
                       return model.parking = formatted || '0';
                     })}
                   />
                 </Grid>
                 <Grid item xs={12}>
                   <Typography variant="subtitle1" color="primary">
          Áreas (metragens)
                   </Typography>
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Área do Terreno m²"
                     disabled={loading}
                     value={model.groundArea}
                     mask="number"
                     validation="string|min:1|max:100"
                     onChange={this.updateModel((model, v) => (model.groundArea = v))}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Área Construída m²"
                     disabled={loading}
                     value={model.buildingArea}
                     mask="number"
                     validation="string|min:1|max:100"
                     onChange={this.updateModel((model, v) => (model.buildingArea = v))}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="ABL m²"
                     disabled={loading}
                     value={model.abl}
                     mask="number"
                     validation="string|min:1|max:100"
                     onChange={this.updateModel((model, v) => (model.abl = v))}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="ABC m²"
                     disabled={loading}
                     value={model.abc}
                     mask="number"
                     validation="string|min:1|max:100"
                     onChange={this.updateModel((model, v) => (model.abc = v))}
                   />
                 </Grid>
                 <Grid item xs={12}>
                   <Typography variant="subtitle1" color="primary">
          Localização
                   </Typography>
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="CEP"
                     disabled={loading}
                     value={cep}
                     mask="zipcode"
                     validation="required|string|min:8|max:9"
                     onChange={this.handleCEP}
                   />
                 </Grid>
                 <Grid item xs={12}>
                   <FieldText
                     label="Endereço"
                     disabled={loading}
                     value={model.address}
                     validation="required|string|min:3|max:1000"
                     onChange={this.updateModel((model, v) => (model.address = v))}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="UF"
                     disabled={loading}
                     value={model.uf}
                     validation="required|string|min:2|max:2"
                     onChange={this.updateModel((model, v) => (model.uf = v))}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <FieldText
                     label="Cidade"
                     disabled={loading}
                     value={model.city}
                     validation="required|string|min:3|max:1000"
                     onChange={this.updateModel((model, v) => (model.city = v))}
                   />
                 </Grid>
               </Grid>
             )}
           </DialogContent>
           <DialogActions className={classes.dialogActions}>
             <Button onClick={onCancel}>Cancelar</Button>
             <Button
               variant={'contained'}
               color="primary"
               type="submit"
               disabled={loading || !!error}
             >
       Salvar
             </Button>
           </DialogActions>
         </FormValidation>
       </FormFieldsContext>
     </Dialog>
   );
 }
}
